import {
  ATTENDANCE_CONSTANTS,
  ActiveAttendanceRules,
  AttendanceRuleGroup,
  AttendanceRuleParams,
  activeAttendanceRules,
} from '@/constant/hr/attendance-rule-catalog';
import {
  formatDateMonthDayYear,
  orgCalendarDate,
  orgWallClock,
  orgWallTimeToUtc,
  parseIsoDate,
  weekdayNameOf,
} from '@/lib/utils/custom-date/custom-date';
import { getOrganizationTimeZone } from '@/lib/utils/custom-date/org-time-zone';
import { DatedWorkDay, workDaysOn } from '@/lib/utils/work-days/work-days';
import { BreakTimeDetail, BreakType } from '../types/attendance.types';

/**
 * The attendance rules, as pure functions — the app's mirror of the attendance workflows' library
 * (blocks/workflows/attendance/lib/day.js), which is the authority: every write goes through a
 * workflow that re-checks these on the server clock. The app uses them to decide what to offer.
 *
 * Which rules bind an employee comes from their permissions only, through the rule catalog
 * (`@/constant/hr/attendance-rule-catalog`): each `hrm::attendance::*` rule permission switches on one
 * rule, and in a rule group only the held rule with the highest priority applies. Never a country.
 * Times are read on the organization's clock (its IAM time zone, see `org-time-zone.ts`).
 */

export interface AttendancePolicy {
  canClock: boolean;
  canTakeBreak: boolean;
  /** MULTIPLE_SESSIONS: a completed day may be clocked in again. */
  multipleSessions: boolean;
  /** PROFILE.OVERTIME_ENABLED. */
  overtime: boolean;
  /** The HR term's flexible hours (not a permission). */
  flexible: boolean;
  /** The active catalog rule of each group. */
  rules: ActiveAttendanceRules;
}

/** A policy with every rule off, for callers that only need a few of them. */
export const EMPTY_ATTENDANCE_POLICY: AttendancePolicy = {
  canClock: false,
  canTakeBreak: false,
  multipleSessions: false,
  overtime: false,
  flexible: false,
  rules: {},
};

/** The parameters of the employee's active rule in `group`, or `null` when no rule of it binds them. */
export const ruleParams = <G extends AttendanceRuleGroup>(
  policy: AttendancePolicy,
  group: G
): AttendanceRuleParams[G] | null =>
  (policy.rules[group]?.params as AttendanceRuleParams[G]) ?? null;

/** Whether the employee may take the lunch break at all: any rule of the `lunch` group binds them. */
export const canTakeLunch = (policy: AttendancePolicy) => Boolean(policy.rules.lunch);

/** Builds a policy from feature flags and the held rule permissions. */
export const attendancePolicyOf = (
  features: Omit<AttendancePolicy, 'rules'>,
  held: (permission: string) => boolean
): AttendancePolicy => ({ ...features, rules: activeAttendanceRules(held) });

/** A rule failure. `message` is a translation key; `params` fill its placeholders;
 * `defaultMessage` is shown when the key has no translation (a workflow's English `Message`). */
export class AttendanceRuleError extends Error {
  constructor(
    translationKey: string,
    readonly params?: Record<string, string | number>,
    readonly defaultMessage?: string
  ) {
    super(translationKey);
    this.name = 'AttendanceRuleError';
  }
}

// ==================== Organization time ====================

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/** Wall-clock hour, minutes since midnight, seconds and weekday of `at` on the organization's clock. */
export const getOrgTime = (
  at: Date
): { hour: number; minutesOfDay: number; secondsOfDay: number; weekday: string } => {
  const { hour, minute, second, weekday } = orgWallClock(at);
  const minutesOfDay = hour * 60 + minute;
  return {
    hour,
    minutesOfDay,
    secondsOfDay: minutesOfDay * 60 + second,
    weekday: WEEKDAY_NAMES[weekday],
  };
};

/** The organization's calendar date of `at`, as "YYYY-MM-DD". */
export const getOrgDateString = (at: Date): string => orgCalendarDate(at.toISOString());

/** The instant of an organization wall-clock time (minutes since midnight) on the day `day` falls on. */
export const orgTimeOnDay = (day: Date, minutesOfDay: number): Date =>
  new Date(
    orgWallTimeToUtc(getOrgDateString(day), Math.floor(minutesOfDay / 60), minutesOfDay % 60)
  );

/** The start of a picked organization day ("YYYY-MM-DD"). */
export const orgDayStart = (day: string): Date => new Date(orgWallTimeToUtc(day, 0, 0));

/** Midnight to 23:59:59.999 of the organization day `now` falls on. */
export const getOrgDayRange = (now: Date = new Date()): { start: Date; end: Date } => {
  const day = getOrgDateString(now);
  return {
    start: orgDayStart(day),
    end: new Date(new Date(orgWallTimeToUtc(day, 23, 59, 59)).getTime() + 999),
  };
};

// ==================== Attendance date ====================
// `AttendanceDate` (and `CorrectionDate`) hold the employee's organization calendar day as that day's
// UTC midnight: a clock-in at 08:30 on 4 Oct in Dhaka is `2026-10-04T00:00:00.000Z`, not the instant
// the day starts there (`2026-10-03T18:00:00Z`). The day therefore reads the same in every zone and
// is matched by plain equality. Clock and break times stay real UTC instants (`*Utc` fields).

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** A calendar day ("YYYY-MM-DD") as the `AttendanceDate` value that stores it. */
export const toAttendanceDate = (day: string): string => `${day}T00:00:00.000Z`;

/** The `AttendanceDate` of the organization day `at` falls on. */
export const attendanceDateOf = (at: Date): string => toAttendanceDate(getOrgDateString(at));

/** The calendar day ("YYYY-MM-DD") a stored `AttendanceDate`/`CorrectionDate` names. */
export const attendanceDayOf = (value?: string | null): string => {
  if (!value) return '';
  if (DATE_ONLY.test(value)) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

/** The instant a stored `AttendanceDate`'s day starts on the organization's clock — the `day` the
 * wall-clock helpers (`orgTimeOnDay`, `toInstantOnDay`, ...) take. */
export const attendanceDayStart = (value: string): Date => orgDayStart(attendanceDayOf(value));

const nextDay = (day: string): string => {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, date + 1)).toISOString().slice(0, 10);
};

/** A `where` clause matching the `AttendanceDate` of `day` ("YYYY-MM-DD"). */
export const attendanceDateClause = (day: string) => ({
  $gte: toAttendanceDate(day),
  $lt: toAttendanceDate(nextDay(day)),
});

/** A `where` clause for `AttendanceDate`s from day `from` to day `to` (both "YYYY-MM-DD", inclusive,
 * either optional); `undefined` when neither is set. */
export const attendanceDateRangeClause = (from?: string, to?: string) => {
  const range: Record<string, string> = {};
  if (from) range.$gte = toAttendanceDate(from);
  if (to) range.$lt = toAttendanceDate(nextDay(to));
  return Object.keys(range).length > 0 ? range : undefined;
};

/** A `where` clause for the `AttendanceDate`s of a calendar month ("YYYY-MM"), first to last day. */
export const attendanceMonthClause = (month: string) => {
  const [year, monthNumber] = month.split('-').map(Number);
  const lastDay = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  return attendanceDateRangeClause(`${month}-01`, `${month}-${String(lastDay).padStart(2, '0')}`);
};

// ==================== Open days and sessions ====================

/** Any stored break entry, as the my- and manage-attendance types read it. */
type StoredBreak = { StartTimeUtc: string; EndTimeUtc?: string | null; Type?: string | null };

type OpenRecord = {
  StartTimeUtc?: string | null;
  EndTimeUtc?: string | null;
  IsOpen?: boolean | null;
  BreakTimeDetails?: StoredBreak[] | null;
};

const isGap = (entry: StoredBreak) => entry.Type === 'ClockOut';

/** The breaks the employee took (Regular and Lunch), without the gaps between sessions. */
export const visibleBreaks = (breaks: BreakTimeDetail[] = []) =>
  breaks.filter((entry) => !isGap(entry));

/**
 * Clocked in and not clocked out: no `EndTimeUtc` yet, or clocked in again (MULTIPLE_SESSIONS) after it,
 * which shows as a ClockOut gap ending after `EndTimeUtc`. Read from the timestamps (as the workflows
 * do); `IsOpen` only serves queries.
 */
export const isOpenRecord = (record?: OpenRecord | null): boolean => {
  if (!record?.StartTimeUtc) return false;
  if (!record.EndTimeUtc) return true;
  return new Date(sessionStartIso(record)).getTime() > new Date(record.EndTimeUtc).getTime();
};

/** The start of the session now running: the end of the last gap, or the day's first clock-in. */
export const sessionStartIso = (record: OpenRecord): string => {
  let start = record.StartTimeUtc as string;
  for (const entry of record.BreakTimeDetails ?? []) {
    if (isGap(entry) && entry.EndTimeUtc && new Date(entry.EndTimeUtc) > new Date(start))
      start = entry.EndTimeUtc;
  }
  return start;
};

/**
 * Whether a day is still being worked: open, and its session recent enough to be running (a night
 * shift included). An older open day is a forgotten clock-out, closed by a correction.
 */
export const isAttendanceInProgress = (record: OpenRecord, now: Date = new Date()): boolean =>
  isOpenRecord(record) &&
  (now.getTime() - new Date(sessionStartIso(record)).getTime()) / 1000 <
    ATTENDANCE_CONSTANTS.openShiftMaxSeconds;

/** A day-type field (`AttendanceDate`, `CorrectionDate`) as "Oct-27-2026, Tuesday", by the day it names. */
export const formatAttendanceDay = (value?: string): string => {
  const day = attendanceDayOf(value);
  const date = formatDateMonthDayYear(parseIsoDate(day));
  const weekday = weekdayNameOf(day);
  return weekday ? `${date}, ${weekday}` : date;
};

const seconds = (fromIso: string, toIso: string) =>
  (new Date(toIso).getTime() - new Date(fromIso).getTime()) / 1000;

const secondsBetween = (from: Date, to: Date) => (to.getTime() - from.getTime()) / 1000;

const toClock = (totalSeconds: number) => {
  const minutes = Math.max(0, Math.round(totalSeconds / 60));
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
};

// ==================== Breaks ====================

export const breakTypeOf = (entry: BreakTimeDetail): BreakType => entry.Type ?? 'Regular';

export const findOpenBreak = (breaks: BreakTimeDetail[]) =>
  breaks.find((entry) => !isGap(entry) && !entry.EndTimeUtc);

export const hasLunchBreak = (breaks: BreakTimeDetail[]) =>
  breaks.some((entry) => breakTypeOf(entry) === 'Lunch');

/** Sum of closed entries in seconds; an open one counts 0. */
const sumClosedSeconds = (entries: BreakTimeDetail[]): number =>
  entries.reduce((total, entry) => {
    if (!entry.EndTimeUtc) return total;
    const length = seconds(entry.StartTimeUtc, entry.EndTimeUtc);
    return Number.isFinite(length) ? total + Math.max(0, length) : total;
  }, 0);

/** Break time the day is charged: closed Regular and Lunch breaks; a lunch rule with
 * `chargeMinimumWithoutLunch` charges at least its counted lunch. */
export const chargedBreakSeconds = (
  policy: AttendancePolicy,
  breaks: BreakTimeDetail[]
): number => {
  const total = sumClosedSeconds(visibleBreaks(breaks));
  const lunch = ruleParams(policy, 'lunch');
  return lunch?.chargeMinimumWithoutLunch ? Math.max(total, lunch.minCountedSeconds) : total;
};

/** When today's lunch may start, or `null` without a lunch rule. `record` is the open day. */
export const lunchWindow = (
  policy: AttendancePolicy,
  record: { StartTimeUtc?: string | null },
  now: Date
): { from: Date; to: Date } | null => {
  const lunch = ruleParams(policy, 'lunch');
  if (!lunch) return null;
  if (lunch.windowFrom === 'clockIn' && record.StartTimeUtc) {
    const clockIn = new Date(record.StartTimeUtc).getTime();
    return {
      from: new Date(clockIn + lunch.fromMinutes * 60000),
      to: new Date(clockIn + lunch.toMinutes * 60000 - 1),
    };
  }
  return {
    from: orgTimeOnDay(now, lunch.fromMinutes),
    to: new Date(orgTimeOnDay(now, lunch.toMinutes).getTime() + 59999),
  };
};

/** Why a break of `type` can't start now, or `null` when it can. `record` is the open day. */
export const getBreakStartBlocker = (
  policy: AttendancePolicy,
  type: BreakType,
  record: { StartTimeUtc?: string | null; BreakTimeDetails?: BreakTimeDetail[] | null },
  now: Date
): string | null => {
  const breaks = record.BreakTimeDetails ?? [];
  if (type === 'Regular' && !policy.canTakeBreak) return 'ATTENDANCE_NO_BREAK_PERMISSION';
  if (type === 'Lunch' && !canTakeLunch(policy)) return 'ATTENDANCE_NO_BREAK_PERMISSION';
  if (findOpenBreak(breaks)) return 'PLEASE_STOP_THE_PREVIOUSLY_STARTED_BREAK_FIRST';
  if (type === 'Lunch') {
    if (hasLunchBreak(breaks)) return 'YOU_HAVE_ALREADY_TAKEN_YOUR_BREAK_FOR_TODAY';
    const window = lunchWindow(policy, record, now);
    if (window && now < window.from) return 'LUNCH_BREAK_NOT_AVAILABLE_YET';
    if (window && now > window.to) return 'LUNCH_BREAK_WINDOW_CLOSED';
  }
  return null;
};

/** Why the open break can't end now, or `null` when it can. */
export const getBreakEndBlocker = (
  policy: AttendancePolicy,
  openBreak: BreakTimeDetail | undefined,
  now: Date
) => {
  if (!openBreak) return 'BREAK_TIME_HAS_NOT_STARTED_YET';
  const minLength = ruleParams(policy, 'minBreakLength');
  if (
    breakTypeOf(openBreak) === 'Regular' &&
    minLength &&
    secondsBetween(new Date(openBreak.StartTimeUtc), now) < minLength.minSeconds
  ) {
    return '15_MINUTE_BREAK_VALIDATION';
  }
  return null;
};

/**
 * Breaks entered by hand (at clock-out or on a correction): each must start before it ends, lie
 * between clock-in and clock-out, and not overlap another; with the MIN_BREAK_LENGTH rule each regular
 * break lasts its minimum. Returns the first failure's translation key.
 */
export const validateEnteredBreaks = (
  policy: AttendancePolicy,
  breaks: BreakTimeDetail[],
  clockIn: Date,
  clockOut: Date
): string | null => {
  const minLength = ruleParams(policy, 'minBreakLength');
  const ranges = breaks.map((entry) => ({
    entry,
    start: new Date(entry.StartTimeUtc).getTime(),
    end: new Date(entry.EndTimeUtc ?? '').getTime(),
  }));
  for (const { entry, start, end } of ranges) {
    if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end) {
      return 'INVALID_BREAK_TIME_EACH_MIN_15_MINUTES_WITHIN_CLOCK_IN_OUT';
    }
    if (start < clockIn.getTime() || end > clockOut.getTime()) {
      return 'INVALID_BREAK_TIME_EACH_MIN_15_MINUTES_WITHIN_CLOCK_IN_OUT';
    }
    if (
      minLength &&
      breakTypeOf(entry) === 'Regular' &&
      (end - start) / 1000 < minLength.minSeconds
    ) {
      return 'INVALID_BREAK_TIME_EACH_MIN_15_MINUTES_WITHIN_CLOCK_IN_OUT';
    }
  }
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i].start < sorted[i - 1].end) return 'INVALID_BREAK_TIME_BREAK_TIMES_CANNOT_OVERLAP';
  }
  return null;
};

// ==================== Day totals ====================

/**
 * Minimum-total-break (breakTiers rule) and maximum-work (maxWork rule) checks for a whole day. A tier
 * covers worked time above `aboveWorkSeconds` up to and including `upToWorkSeconds`. `enforceMaxWork`
 * is on for corrections only: a live clock-out past the limit is still recorded.
 */
export const validateDayTotals = (
  policy: AttendancePolicy,
  workSeconds: number,
  breakSeconds: number,
  enforceMaxWork: boolean
): string | null => {
  for (const tier of ruleParams(policy, 'breakTiers')?.tiers ?? []) {
    const inTier =
      workSeconds > tier.aboveWorkSeconds &&
      (tier.upToWorkSeconds === undefined || workSeconds <= tier.upToWorkSeconds);
    if (inTier && breakSeconds < tier.minBreakSeconds) return tier.messageKey;
  }
  const maxWork = ruleParams(policy, 'maxWork');
  if (enforceMaxWork && maxWork && workSeconds > maxWork.maxWorkSeconds) {
    return 'WORK_TIME_CANNOT_EXCEED_10_HOURS';
  }
  return null;
};

// ==================== Clock in / out ====================

export const isPastClockInCutoff = (policy: AttendancePolicy, now: Date): boolean => {
  const cutoff = ruleParams(policy, 'clockInCutoff');
  if (!cutoff || (cutoff.exemptFlexible && policy.flexible)) return false;
  return getOrgTime(now).minutesOfDay >= cutoff.cutoffMinutes;
};

/**
 * Whether the employee may clock in from this screen today. A work-from-home employee always may;
 * an office worker may not on an office day: a day listed in `WFODays`, and every day when none is
 * listed (legacy `!IsWFH && (WFODays?.Contains(today) ?? true)`). Mirrors `isOfficeDay` in
 * blocks/workflows/attendance/lib/day.js.
 */
export const canSelfClockInToday = (
  isWFH: boolean | undefined,
  wfoDays: string[] | null | undefined,
  now: Date
): boolean => {
  if (isWFH) return true;
  if (!wfoDays || wfoDays.length === 0) return false;
  return !wfoDays.includes(getOrgTime(now).weekday);
};

/** Seconds left before the running session may be clocked out (clockOutWait rule); 0 once it may. */
export const secondsUntilClockOut = (
  policy: AttendancePolicy,
  sessionStart: string,
  now: Date
): number => {
  const wait = ruleParams(policy, 'clockOutWait');
  if (!wait) return 0;
  return Math.max(0, wait.waitSeconds - secondsBetween(new Date(sessionStart), now));
};

/**
 * officeNetwork rule. A workflow can't see the caller's address and the browser's own report proves
 * nothing, so: no approved address configured means the rule is off; with addresses configured, every
 * employee it binds (not working from home) is refused until the platform passes the address.
 */
export const officeNetworkBlocker = (policy: AttendancePolicy, isWFH: boolean | undefined) => {
  const network = ruleParams(policy, 'officeNetwork');
  if (!network || isWFH || network.approvedIps.length === 0) return null;
  return 'OFFICE_NETWORK_UNVERIFIABLE';
};

/** A wait as "01hr(s) 05min(s)" (rounded to the minute), the `{{time}}` of the clock-out countdown. */
export const formatWaitTime = (totalSeconds: number) => {
  const [hours, minutes] = toClock(totalSeconds).split(':');
  return `${hours}hr(s) ${minutes}min(s)`;
};

/** `HH:mm` rendering of a stored `*InSecond` duration, e.g. `TotalWorkTimeInSecond`. */
export const formatDuration = toClock;

/** Signed "HH:mm": a day's overtime is worked minus expected time, so undertime is negative. */
export const formatSignedDuration = (totalSeconds: number) =>
  `${totalSeconds < 0 ? '-' : ''}${toClock(Math.abs(totalSeconds))}`;

const WALL_CLOCK_TIME = /^(\d{1,2}):(\d{2})$/;
const pad2 = (value: number) => String(value).padStart(2, '0');

/** "HH:mm" (or "HH:mm:ss") of minutes / seconds since midnight. */
const wallClockText = (secondsOfDay: number, withSeconds: boolean) => {
  const text = `${pad2(Math.floor(secondsOfDay / 3600))}:${pad2(Math.floor((secondsOfDay % 3600) / 60))}`;
  return withSeconds ? `${text}:${pad2(secondsOfDay % 60)}` : text;
};

/**
 * A correction time as an instant on the organization day `day` falls on. A bare "HH:mm" (what the
 * form collects, and what older corrections stored) before `after` belongs to the next day:
 * a shift that runs past midnight. A value that already is an instant passes through unchanged.
 */
export const toInstantOnDay = (value: string, day: Date, after?: string): string => {
  const match = WALL_CLOCK_TIME.exec(value);
  if (!match) return value;
  const minutesOfDay = Number(match[1]) * 60 + Number(match[2]);
  const sameDay = orgTimeOnDay(day, minutesOfDay);
  if (!after || sameDay.getTime() >= new Date(after).getTime()) return sameDay.toISOString();
  // 36 hours on from the day's start is always inside the next day, whatever DST does.
  return orgTimeOnDay(
    new Date(orgDayStart(getOrgDateString(day)).getTime() + 36 * 3600 * 1000),
    minutesOfDay
  ).toISOString();
};

/**
 * A correction's times as instants on its day: a clock-out or break time before the clock-in is on
 * the next day (see `toInstantOnDay`). The form never lets clock-in and clock-out be equal.
 */
export const correctionTimesOnDay = (
  day: Date,
  times: { StartTimeUtc: string; EndTimeUtc: string; BreakTimeDetails: BreakTimeDetail[] }
) => {
  const StartTimeUtc = toInstantOnDay(times.StartTimeUtc, day);
  const onShift = (value: string) => toInstantOnDay(value, day, StartTimeUtc);
  return {
    StartTimeUtc,
    EndTimeUtc: onShift(times.EndTimeUtc),
    BreakTimeDetails: times.BreakTimeDetails.map((entry) => ({
      ...entry,
      StartTimeUtc: onShift(entry.StartTimeUtc),
      ...(entry.EndTimeUtc ? { EndTimeUtc: onShift(entry.EndTimeUtc) } : {}),
    })),
  };
};

/** The zone a record's times are shown in: its own `Timezone`, or the organization's for older
 * records saved without one. */
const displayZone = (timeZone?: string | null): string => timeZone || getOrganizationTimeZone();

/** A shift's end time for display, marked "(+1)" when it falls on the day after its start. Shown in
 * `timeZone`, the record's own `Timezone` (see `formatTimeOfDay`). */
export const formatShiftEnd = (
  start?: string,
  end?: string,
  withSeconds = false,
  timeZone?: string | null
): string => {
  const text = formatTimeOfDay(end, withSeconds, timeZone);
  if (!start || !end || WALL_CLOCK_TIME.test(start) || WALL_CLOCK_TIME.test(end)) return text;
  const zone = displayZone(timeZone);
  return orgCalendarDate(end, zone) > orgCalendarDate(start, zone) ? `${text} (+1)` : text;
};

/** "HH:mm" for display, from either a stored instant or an older bare "HH:mm". An instant is shown
 * in `timeZone`, the record's own `Timezone` (the zone it was recorded in), or the organization's
 * zone when the record has none. */
export const formatTimeOfDay = (
  value?: string,
  withSeconds = false,
  timeZone?: string | null
): string => {
  if (!value) return '-';
  if (WALL_CLOCK_TIME.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const { hour, minute, second } = orgWallClock(date, displayZone(timeZone));
  return wallClockText((hour * 60 + minute) * 60 + second, withSeconds);
};

// ==================== Corrections ====================

/** One row of an HR term's `WorkDays`, with its hours in decimal (8.5 = 8 h 30 min). */
export interface TermWorkDay extends DatedWorkDay {
  WorkingHours: number;
}

/** A rule failure as a translation key and its placeholders. */
export interface RuleBlocker {
  key: string;
  params?: Record<string, string>;
}

/** The work seconds the HR term schedules on `day` ("YYYY-MM-DD"): its weekday's hours in the work days
 * in force that day; 0 for a weekday without hours. */
export const scheduledWorkSecondsOn = (
  workDays: readonly TermWorkDay[] | null | undefined,
  day: string
): number => {
  const weekday = weekdayNameOf(day);
  const row = workDaysOn(workDays, day).find((entry) => entry.WorkDay === weekday);
  return Math.round(Number(row?.WorkingHours ?? 0) * 3600);
};

/**
 * correctionMinHours rule: why a correction of `clockSeconds` on `day` can't be requested as `type`, or
 * null. The minimum is a share of the day's own hours in the HR term (`workDays`); leave and holidays
 * don't lower it, since a half day is requested as a half day. A weekday without hours has no full or
 * half day to correct. The same rule as the workflows' `correctionMinimumBlocker`.
 */
export const correctionMinimumBlocker = (
  policy: AttendancePolicy,
  clockSeconds: number,
  type: 'fullday' | 'halfday',
  workDays: readonly TermWorkDay[] | null | undefined,
  day: string
): RuleBlocker | null => {
  const minimum = ruleParams(policy, 'correctionMinHours');
  if (!minimum) return null;
  if (!(workDays ?? []).some((entry) => Number(entry?.WorkingHours ?? 0) > 0)) {
    return { key: 'ATTENDANCE_NO_WORK_DAYS' };
  }
  const daySeconds = scheduledWorkSecondsOn(workDays, day);
  if (daySeconds <= 0) return { key: 'ATTENDANCE_CORRECTION_NOT_A_WORK_DAY' };
  const full = type === 'fullday';
  const required = Math.round(
    (daySeconds * (full ? minimum.fullDayPercent : minimum.halfDayPercent)) / 100
  );
  if (clockSeconds >= required) return null;
  return {
    key: full ? 'FULL_DAY_CORRECTION_REQUIRES_MIN_HOURS' : 'HALF_DAY_CORRECTION_REQUIRES_MIN_HOURS',
    params: { hours: toClock(required) },
  };
};
